// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE OUTFORM PRICING SHEET — the same instrument Hank built for Air Duct
// (airduct/calculator.tsx) and Ruby reused for Everything 420. Every blue number
// is an input, totals recompute live, nothing is stored. A what-if surface.
//
// WHAT IS DIFFERENT HERE, AND IT IS THE WHOLE POINT: this list is not our
// invention and not a discovery of ours. It is THEIR 24-page blueprint, parsed
// into 468 numbered requirements (agents/quinn/outform-app-spec/requirements.json),
// and every single one of those 468 is assigned to exactly one row below. The
// REQS column is the count, machine-verified — 286 in the alpha, 182 in the full
// version, none duplicated and none dropped.
//
// That matters commercially. A steering committee comparing two quotes cannot
// compare scope unless both are expressed in the same units, and the only units
// everyone in that room already agrees on are the section numbers of their own
// document. So the scope argument is over before it starts.
//
// THE SPLIT. Erez defined the core himself: an estimator adds a client brief and
// gets an analysis page, a ballpark and a detailed estimate — all in DRAFT,
// waiting for review. Everything strictly required to make that loop run is the
// alpha. Everything that makes it a production system but is not needed for the
// loop to work is the full version.
//
// ALPHA DEPTH. The alpha builds the core of each alpha feature, not all of it:
// one display type, seeded reference data, one read-only source, no SOC2, no
// retraining. `alphaDepth` is that fraction, and the remainder is billed in the
// full phase as hardening. One honest knob rather than a second set of sizes.
//
// RATES. S/M/L are the same house rates the other two sheets use — the scope
// moves between clients, never the rate. XL is new and is not a rate change: a
// 63-requirement engine is genuinely a bigger thing than a 4-requirement
// workflow, and pretending otherwise is how a build gets underpriced. The
// enterprise multiplier is the one real Outform-specific factor — audit trail,
// four RBAC tiers, named integrations, a security posture Air Duct never had.
//
// 🔴 in a cell = our estimate, not something they stated.
//
// Calibrated to land the full version near $270,000 — under 60% of the ~$450,000
// the incumbent's six-month team shape models out at, while still unmistakably
// an enterprise contract rather than a freelancer's quote.
//
// Owner agent: Quinn.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useMemo } from "react";
import { N, NFONT, NMONO, R, R_CTL } from "./notion";

const F = NFONT;
const MONO = NMONO;

const C = {
  ink: N.ink, ink2: N.ink2, ink3: N.ink3,
  line: N.line, lineHard: N.lineStrong,
  bg: N.bg, head: N.bgGray, zebra: N.bg,
  blue: N.blue, blueSoft: N.blueBg,
  green: N.green, greenSoft: N.greenBg,
  amber: N.yellow, amberSoft: N.yellowBg,
  red: N.red, redSoft: N.redBg,
  purple: N.purple, purpleSoft: N.purpleBg,
};

/** "?" = cannot be sized until we look, never quoted fixed.
 *  "—" = a real requirement of theirs that is NOT a build line: it is met by the
 *  engagement itself, or it is a problem statement the whole system answers.
 *  Both are shown rather than hidden, because 468 has to add up. */
type Size = "S" | "M" | "L" | "XL" | "?" | "—";
type Phase = "alpha" | "full";

type Feature = {
  id: string;
  name: string;
  note: string;
  /** Which numbered sections of THEIR document this row covers. */
  refs: string;
  /** How many of the 468 requirements land here. Machine-counted. */
  reqs: number;
  size: Size;
  /** Who OPENS the screen. 0 = a mechanism with no screen at all. */
  users: number | null;
  est: boolean;
  phase: Phase;
};

/** Every row traces to a section of Rev 10. The REQS column sums to 468.
 *  Alpha = the loop Erez specified. Full = everything else they asked for. */
const FEATURES: Feature[] = [
  // ── ALPHA · the core loop: brief in → analysis + ballpark + detailed, in draft
  { id: "A1", name: "The job record and the corpus", refs: "§6 · §2 structured layer · §1 raw assets", reqs: 57,
    note: "every §6 field as a real column, plus the five their §6 does not store — outcome, competitor price, loss reason, push-back, negotiation rounds",
    size: "L", users: 6, est: true, phase: "alpha" },
  { id: "A2", name: "Ingest 1,500 jobs out of Smartsheet", refs: "no section — see note", reqs: 0,
    note: "ZERO of their 468 requirements describe this, and that is the finding: their §3.5 files the corpus as a database when it is a process. Nothing else works until it runs",
    size: "XL", users: 0, est: true, phase: "alpha" },
  { id: "A3", name: "Brief in, analysis out", refs: "§4 query & retrieval · §3 operating model", reqs: 13,
    note: "what it searched, what came back, what it compared, what it concluded, what is still open — before the price, never after it",
    size: "M", users: 6, est: true, phase: "alpha" },
  { id: "A4", name: "Ballpark engine", refs: "§3.1 — all seven groups", reqs: 63,
    note: "comps by attribute similarity, cost curves, every manufacturing route priced and any route past the deadline excluded with the exclusion stated",
    size: "XL", users: 25, est: true, phase: "alpha" },
  { id: "A5", name: "Detailed estimate engine", refs: "§3.2 — all eight groups", reqs: 81,
    note: "BoM with a target price per component, sourcing plan, missing-cost risk, validity statement. 🔴 depends on the Excel their §3.2 calls the calculation core — still unseen",
    size: "XL", users: 6, est: true, phase: "alpha" },
  { id: "A6", name: "Perceived-value bands", refs: "§5 bands · §2 project attributes", reqs: 9,
    note: "their four published bands as data rather than constants, because they will change them",
    size: "S", users: 2, est: false, phase: "alpha" },
  { id: "A7", name: "Three prices, one screen", refs: "§7 output & dashboard module", reqs: 7,
    note: "our cost, the perceived-value price, and the target-price scenario when a client budget is entered — plus the confidence band and its drivers",
    size: "M", users: 25, est: true, phase: "alpha" },
  { id: "A8", name: "Draft → estimator review, with an audit trail", refs: "§4 RBAC & audit · human-in-the-loop", reqs: 14,
    note: "the half of the loop Erez specified: nothing the agent writes is fact until an estimator says so, and every action is logged with actor and before/after",
    size: "M", users: 6, est: false, phase: "alpha" },
  { id: "A9", name: "Conversion probability score", refs: "§3.7 · §6 pricing actions · win/loss · client behaviour · market", reqs: 40,
    note: "their published 40/30/20/10 weights, and the refusal when the data is not there. 🔴 accuracy depends entirely on whether Smartsheet records outcomes",
    size: "L", users: 25, est: true, phase: "alpha" },
  { id: "A10", name: "Smartsheet, read-only", refs: "§9 integration architecture", reqs: 2,
    note: "one source, one direction. Everything the alpha runs on comes through here",
    size: "M", users: 0, est: false, phase: "alpha" },

  // ── FULL · everything else they asked for
  { id: "F1", name: "Vendor pricing refresh engine", refs: "§3.3 — all seven groups", reqs: 71,
    note: "monthly RFQs out, PDF/Excel/email responses parsed back in, scorecards, cost-curve recalibration. The largest single block in their document after the two proposals",
    size: "XL", users: 0, est: true, phase: "full" },
  { id: "F2", name: "AI-generated costing template", refs: "§3.6", reqs: 8,
    note: "the vendor-ready RFP pack Procurement sends, anonymised per §10.2",
    size: "M", users: 6, est: true, phase: "full" },
  { id: "F3", name: "The seven reference tables, on their cadence", refs: "§10.1 · §5 market conditions", reqs: 8,
    note: "LED, raw material, tariff, freight, labour, vendor scorecard, perceived-value attributes — each with the owner and refresh cycle §10.1 assigns it",
    size: "M", users: 6, est: false, phase: "full" },
  { id: "F4", name: "D365 · Sugar · Fabric · Smartsheet, bidirectional", refs: "§9 systems", reqs: 4,
    note: "🔴 their own §11 defers complex integration to phase 3, and §9 names Monday, Exact and Priority on the same page — the count may be higher than four",
    size: "L", users: 0, est: true, phase: "full" },
  { id: "F5", name: "The eleven named agents", refs: "§8", reqs: 11,
    note: "the ones the core loop does not need: freight, market intelligence, white-label RFQ, estimate validation",
    size: "L", users: 0, est: true, phase: "full" },
  { id: "F6", name: "Visual similarity — drawing and photo embeddings", refs: "§3 visual similarity index", reqs: 3,
    note: "cannot be fixed-price until we see the real mix of PDF against DXF/DWG. Vector CAD does not embed the way a photo does, and that is an open design decision, not a solved one",
    size: "?", users: null, est: true, phase: "full" },
  { id: "F7", name: "Governance: security, data quality, retraining, adoption", refs: "§4 — five remaining groups", reqs: 25,
    note: "SOC2-aligned controls, 7-year retention, mandatory-field enforcement, monthly and quarterly retraining, the training and pilot rollout",
    size: "L", users: 25, est: true, phase: "full" },
  { id: "F8", name: "Success metrics, and the baselines that do not exist", refs: "§12 · §12.1", reqs: 16,
    note: "four of their nine metrics have no baseline at all. Measuring them is the first week's work, not the last",
    size: "M", users: 25, est: false, phase: "full" },
  { id: "F9", name: "Programme governance", refs: "§11 risks · §13.1 · §13.2", reqs: 20,
    note: "steering committee, cross-functional dependencies, the risk register. Met by how the engagement is run — we refuse to bill it as software",
    size: "—", users: null, est: false, phase: "full" },
  { id: "F10", name: "Problem statement and executive summary", refs: "§1 · §2.1", reqs: 16,
    note: "the five operational challenges and the strategic pricing gap. Answered by the system as a whole; charging for a paragraph would be padding",
    size: "—", users: null, est: false, phase: "full" },
];

/** Calibrated to land the full version near $270,000. S/M/L are the house rates
 *  the Air Duct and Everything 420 sheets already use. */
const DEFAULT_KNOBS = {
  S: 3500, M: 8000, L: 16000, XL: 24000,
  b1: 1.0, b2: 1.15, b3: 1.3, b4: 1.45,
  ent: 1.15,
  alphaDepth: 0.45,
  platform: 12000, lake: 10000,
  connectorPrice: 6000, connectorCount: 4,
  supportFull: 20, supportHybrid: 12, supportHandover: 8,
  incumbent: 450000,
};

type Knobs = typeof DEFAULT_KNOBS;

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

function bandOf(users: number | null, k: Knobs): { m: number; label: string } {
  if (users == null) return { m: 0, label: "—" };
  if (users === 0) return { m: k.b1, label: "no screen" };
  if (users <= 2) return { m: k.b1, label: "1-2" };
  if (users <= 10) return { m: k.b2, label: "3-10" };
  if (users <= 30) return { m: k.b3, label: "11-30" };
  return { m: k.b4, label: "31+" };
}

function basePrice(size: Size, k: Knobs): number | null {
  if (size === "S") return k.S;
  if (size === "M") return k.M;
  if (size === "L") return k.L;
  if (size === "XL") return k.XL;
  return null; // "?" not sizeable · "—" not a build line
}

function Num({ value, onChange, width = 78, step = 100, prefix, suffix }: {
  value: number; onChange: (n: number) => void; width?: number;
  step?: number; prefix?: string; suffix?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {prefix && <span style={{ color: C.blue, fontFamily: MONO, fontSize: 12.5 }}>{prefix}</span>}
      <input
        type="number" value={value} step={step}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        style={{
          width, fontFamily: MONO, fontSize: 12.5, fontWeight: 500, color: N.blue,
          background: N.blueBg, border: "1px solid transparent", borderRadius: R_CTL,
          padding: "3px 6px", textAlign: "right", outline: "none",
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
      {suffix && <span style={{ color: C.ink3, fontFamily: MONO, fontSize: 11.5 }}>{suffix}</span>}
    </span>
  );
}

function Th({ children, align = "left", w }: { children: React.ReactNode; align?: "left" | "right" | "center"; w?: number }) {
  return (
    <th style={{
      textAlign: align, width: w, padding: "9px 12px", fontFamily: MONO, fontSize: 10.5,
      fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: C.ink2,
      background: N.bgGray, borderBottom: `1px solid ${N.line}`, whiteSpace: "nowrap",
    }}>{children}</th>
  );
}

function Td({ children, align = "left", mono, bold, color }: {
  children: React.ReactNode; align?: "left" | "right" | "center";
  mono?: boolean; bold?: boolean; color?: string;
}) {
  return (
    <td style={{
      textAlign: align, padding: "8px 12px", fontSize: mono ? 13 : 13.5,
      fontFamily: mono ? MONO : F, fontWeight: bold ? 700 : 500,
      color: color ?? C.ink, borderBottom: `1px solid ${C.line}`, verticalAlign: "middle",
    }}>{children}</td>
  );
}

function KnobRow({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "5px 0" }}>
      <span style={{ fontSize: 12.5, color: C.ink2 }}>
        {label}{hint && <span style={{ color: C.ink3, fontSize: 11 }}> {hint}</span>}
      </span>
      {children}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: N.bg, border: `1px solid ${N.line}`, borderRadius: R, padding: "12px 14px" }}>
      <div style={{
        fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em",
        textTransform: "uppercase", color: C.ink3, marginBottom: 6,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Total({ label, value, sub, tone }: {
  label: string; value: string; sub: string; tone: "green" | "blue" | "ink" | "purple";
}) {
  const map = {
    green: { bg: N.greenBg, ink: N.green },
    blue: { bg: N.blueBg, ink: N.blue },
    purple: { bg: N.purpleBg, ink: N.purple },
    ink: { bg: N.grayBg, ink: N.ink },
  }[tone];
  return (
    <div style={{ flex: 1, background: map.bg, borderRadius: R, padding: "13px 16px", minWidth: 180 }}>
      <div style={{
        fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".1em",
        textTransform: "uppercase", color: map.ink, opacity: .75,
      }}>{label}</div>
      <div style={{ fontFamily: F, fontSize: 28, fontWeight: 600, color: map.ink, letterSpacing: "-.02em", lineHeight: 1.15, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: C.ink2, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

export function OutformPricingSheet() {
  const [k, setK] = useState<Knobs>(DEFAULT_KNOBS);
  const [rows, setRows] = useState<Feature[]>(FEATURES);

  const set = (key: keyof Knobs) => (n: number) => setK((p) => ({ ...p, [key]: n }));
  const patch = (id: string, d: Partial<Feature>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...d } : r)));

  const calc = useMemo(() => {
    const setup = k.platform + k.lake + k.connectorPrice * k.connectorCount;
    const priced = rows.map((r) => {
      const base = basePrice(r.size, k);
      const band = bandOf(r.users, k);
      const price = base == null || band.m === 0 ? null : base * band.m * k.ent;
      return { ...r, base, band, price };
    });
    const alphaGross = priced.reduce((s, r) => s + (r.phase === "alpha" ? r.price ?? 0 : 0), 0);
    const fullOnly = priced.reduce((s, r) => s + (r.phase === "full" ? r.price ?? 0 : 0), 0);
    const alphaValue = alphaGross * k.alphaDepth;
    const hardening = alphaGross * (1 - k.alphaDepth);
    const full = hardening + fullOnly + setup;

    const reqAlpha = priced.reduce((s, r) => s + (r.phase === "alpha" ? r.reqs : 0), 0);
    const reqFull = priced.reduce((s, r) => s + (r.phase === "full" ? r.reqs : 0), 0);
    const unsized = priced.filter((r) => r.size === "?").length;
    const notBuild = priced.filter((r) => r.size === "—").reduce((s, r) => s + r.reqs, 0);

    return {
      setup, priced, alphaGross, alphaValue, hardening, fullOnly, full,
      reqAlpha, reqFull, reqTotal: reqAlpha + reqFull, unsized, notBuild,
      programme: alphaValue === 0 ? full : full, // alpha is free; the programme IS the full number
      perReq: full / Math.max(1, reqAlpha + reqFull - notBuild),
      vsIncumbent: k.incumbent ? (full / k.incumbent) * 100 : null,
    };
  }, [k, rows]);

  const sizeBtn = (r: Feature, s: Size) => {
    const on = r.size === s;
    return (
      <button
        key={s}
        onClick={() => patch(r.id, { size: s })}
        style={{
          width: 28, height: 24, borderRadius: R_CTL, cursor: "pointer",
          fontFamily: MONO, fontSize: 11, fontWeight: 600,
          border: `1px solid ${on ? "transparent" : N.lineSoft}`,
          background: on ? (s === "?" ? N.red : s === "—" ? N.gray : N.ink) : N.bg,
          color: on ? "#FFF" : N.ink3,
        }}
      >{s}</button>
    );
  };

  return (
    // nowheel/nodrag/nopan + stopPropagation is MANDATORY on an interactive board
    // node: without it React Flow's panOnDrag swallows the pointer before it
    // reaches an input, and nothing can be clicked while everything looks right.
    <div
      className="nowheel nodrag nopan"
      data-zoom
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        pointerEvents: "auto", width: 1720, background: N.bg,
        border: `1px solid ${N.line}`, borderRadius: R, padding: 26,
        fontFamily: F, boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 700, color: N.ink, letterSpacing: "-.02em" }}>
            The pricing sheet — alpha and full version
          </div>
          <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 3 }}>
            Every <span style={{ color: C.blue, fontWeight: 700 }}>blue</span> number is editable. Totals recompute live. Nothing is saved.
            {" "}All <b>{calc.reqTotal}</b> requirements of their Rev 10 are assigned to exactly one row — <b>{calc.reqAlpha}</b> alpha, <b>{calc.reqFull}</b> full.
          </div>
        </div>
        <button
          onClick={() => { setK(DEFAULT_KNOBS); setRows(FEATURES); }}
          style={{
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
            color: N.ink2, background: N.bg, border: `1px solid ${N.lineStrong}`,
            borderRadius: R_CTL, padding: "6px 12px", cursor: "pointer",
          }}
        >Reset to calibrated</button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Total tone="green" label="Alpha — what we give away" value={usd(calc.alphaValue)}
          sub={`${calc.reqAlpha} requirements at ${Math.round(k.alphaDepth * 100)}% depth · charged at $0`} />
        <Total tone="blue" label="Full version — what we charge" value={usd(calc.full)}
          sub={`hardening ${usd(calc.hardening)} + ${usd(calc.fullOnly)} new + ${usd(calc.setup)} setup`} />
        <Total tone="purple" label="Against the incumbent" value={calc.vsIncumbent ? `${Math.round(calc.vsIncumbent)}%` : "—"}
          sub={`of the modelled ${usd(k.incumbent)} six-month quote`} />
        <Total tone="ink" label="Per requirement" value={usd(calc.perReq)}
          sub={`over ${calc.reqTotal - calc.notBuild} buildable · ${calc.notBuild} met by the engagement`} />
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <Panel title="Feature base price">
            <KnobRow label="Small"><Num value={k.S} onChange={set("S")} step={500} prefix="$" /></KnobRow>
            <KnobRow label="Medium"><Num value={k.M} onChange={set("M")} step={500} prefix="$" /></KnobRow>
            <KnobRow label="Large"><Num value={k.L} onChange={set("L")} step={1000} prefix="$" /></KnobRow>
            <KnobRow label="Extra large"><Num value={k.XL} onChange={set("XL")} step={1000} prefix="$" /></KnobRow>
            <div style={{ fontSize: 11, color: C.ink3, lineHeight: 1.45, marginTop: 5, borderTop: `1px solid ${C.line}`, paddingTop: 6 }}>
              S, M and L are the same rates quoted to Air Duct and Everything 420. One rate across clients — the scope moves, never the rate.
            </div>
          </Panel>

          <Panel title="User bands — capped multiplier">
            <KnobRow label="1-2 users"><Num value={k.b1} onChange={set("b1")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="3-10 users"><Num value={k.b2} onChange={set("b2")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="11-30 users"><Num value={k.b3} onChange={set("b3")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="31+ users"><Num value={k.b4} onChange={set("b4")} step={0.05} width={62} suffix="x" /></KnobRow>
          </Panel>

          <Panel title="Outform-specific">
            <KnobRow label="Enterprise factor" hint="audit · RBAC · security"><Num value={k.ent} onChange={set("ent")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="Alpha depth"><Num value={k.alphaDepth} onChange={set("alphaDepth")} step={0.05} width={62} suffix="x" /></KnobRow>
            <div style={{ fontSize: 11, color: C.ink3, lineHeight: 1.45, marginTop: 4 }}>
              Alpha depth is how much of each core feature the free three weeks actually builds. The rest is hardening, billed in the full phase.
            </div>
          </Panel>

          <Panel title="Setup">
            <KnobRow label="Platform on their infra"><Num value={k.platform} onChange={set("platform")} step={1000} prefix="$" /></KnobRow>
            <KnobRow label="Fabric lake + medallion"><Num value={k.lake} onChange={set("lake")} step={1000} prefix="$" /></KnobRow>
            <KnobRow label="Per integration"><Num value={k.connectorPrice} onChange={set("connectorPrice")} step={500} prefix="$" /></KnobRow>
            <KnobRow label="Integrations" hint="🔴 4 named, may be more"><Num value={k.connectorCount} onChange={set("connectorCount")} step={1} width={62} suffix="x" /></KnobRow>
            <div style={{
              display: "flex", justifyContent: "space-between", marginTop: 7,
              borderTop: `1.5px solid ${C.lineHard}`, paddingTop: 7,
              fontFamily: MONO, fontSize: 13, fontWeight: 700, color: C.ink,
            }}>
              <span>SETUP</span><span>{usd(calc.setup)}</span>
            </div>
          </Panel>

          <Panel title="Support — Damian's three models">
            <KnobRow label="We support it all"><Num value={k.supportFull} onChange={set("supportFull")} step={1} width={54} suffix="%/yr" /></KnobRow>
            <KnobRow label="Hybrid + escalation"><Num value={k.supportHybrid} onChange={set("supportHybrid")} step={1} width={54} suffix="%/yr" /></KnobRow>
            <KnobRow label="Hyper-care → handover"><Num value={k.supportHandover} onChange={set("supportHandover")} step={1} width={54} suffix="once" /></KnobRow>
            <div style={{ fontSize: 11.5, color: C.ink2, lineHeight: 1.6, marginTop: 6, borderTop: `1px solid ${C.line}`, paddingTop: 6 }}>
              <div>Full · <b>{usd(calc.full * k.supportFull / 100)}</b>/yr &nbsp;({usd(calc.full * k.supportFull / 1200)}/mo)</div>
              <div>Hybrid · <b>{usd(calc.full * k.supportHybrid / 100)}</b>/yr &nbsp;({usd(calc.full * k.supportHybrid / 1200)}/mo)</div>
              <div>Handover · <b>{usd(calc.full * k.supportHandover / 100)}</b> once, then theirs</div>
              <div style={{ color: C.ink3, marginTop: 4 }}>Anchored on the 15–20% of build cost that is the published industry norm.</div>
            </div>
          </Panel>

          <Panel title="Their alternative">
            <KnobRow label="Incumbent, modelled"><Num value={k.incumbent} onChange={set("incumbent")} step={10000} prefix="$" /></KnobRow>
            <div style={{ fontSize: 11, color: C.ink3, lineHeight: 1.45, marginTop: 4 }}>
              🔴 Ours, not theirs. Two developers plus fractional architect, PM and account manager over six months at published 2026 US rates.
            </div>
          </Panel>
        </div>

        <div style={{ flex: 1, background: N.bg, border: `1px solid ${N.line}`, borderRadius: R, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th w={54} align="center">Phase</Th>
                <Th>Feature</Th>
                <Th w={62} align="right">Reqs</Th>
                <Th w={132} align="center">Size</Th>
                <Th w={82} align="right">Base</Th>
                <Th w={84} align="center">Users</Th>
                <Th w={70} align="center">Band</Th>
                <Th w={100} align="right">Price</Th>
              </tr>
            </thead>
            <tbody>
              {calc.priced.map((r, i) => {
                const notBuilt = r.size === "—";
                const unsized = r.size === "?";
                const bg = unsized ? C.redSoft : notBuilt ? N.bgGray : i % 2 ? C.zebra : C.bg;
                return (
                  <tr key={r.id} style={{ background: bg }}>
                    <Td align="center">
                      <button
                        onClick={() => patch(r.id, { phase: r.phase === "alpha" ? "full" : "alpha" })}
                        style={{
                          fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".05em",
                          padding: "3px 7px", borderRadius: R_CTL, cursor: "pointer",
                          border: "1px solid transparent",
                          background: r.phase === "alpha" ? N.greenBg : N.blueBg,
                          color: r.phase === "alpha" ? N.green : N.blue,
                        }}
                      >{r.phase === "alpha" ? "ALPHA" : "FULL"}</button>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 650, color: unsized ? C.red : C.ink }}>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: C.ink3, marginRight: 6 }}>{r.id}</span>
                        {r.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 1, lineHeight: 1.35 }}>{r.note}</div>
                      <div style={{ fontFamily: MONO, fontSize: 10.5, color: C.ink3, marginTop: 2 }}>{r.refs}</div>
                    </Td>
                    <Td align="right" mono bold color={r.reqs === 0 ? C.red : C.ink2}>{r.reqs}</Td>
                    <Td align="center">
                      <div style={{ display: "inline-flex", gap: 3 }}>
                        {(["S", "M", "L", "XL", "?", "—"] as Size[]).map((s) => sizeBtn(r, s))}
                      </div>
                    </Td>
                    <Td align="right" mono color={r.base == null ? C.ink3 : C.ink2}>
                      {r.base == null ? "—" : usd(r.base)}
                    </Td>
                    <Td align="center">
                      {r.users == null ? (
                        <span style={{ fontFamily: MONO, fontSize: 11.5, color: unsized ? C.red : C.ink3 }}>
                          {unsized ? "🔴 ?" : "n/a"}
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          {r.est && <span style={{ fontSize: 10 }} title="our estimate, not stated by the client">🔴</span>}
                          <Num value={r.users} onChange={(n) => patch(r.id, { users: Math.max(0, Math.round(n)) })} step={1} width={46} />
                        </span>
                      )}
                    </Td>
                    <Td align="center" mono color={C.ink2}>{r.band.m === 0 ? "—" : `${r.band.m}x`}</Td>
                    <Td align="right" mono bold color={unsized ? C.red : notBuilt ? C.ink3 : C.ink}>
                      {r.price == null ? (unsized ? "not quoted" : "engagement") : usd(r.price)}
                    </Td>
                  </tr>
                );
              })}
              <tr style={{ background: C.head }}>
                <Td align="center"><span style={{ fontFamily: MONO, fontSize: 11, color: C.ink2, fontWeight: 700 }}>ALL</span></Td>
                <Td bold>Totals</Td>
                <Td align="right" mono bold>{calc.reqTotal}</Td>
                <Td align="center" mono color={C.ink3}>{calc.unsized} unsized</Td>
                <Td align="right" mono color={C.ink3}>—</Td>
                <Td align="center" mono color={C.ink3}>—</Td>
                <Td align="center" mono color={C.ink3}>—</Td>
                <Td align="right" mono bold>{usd(calc.alphaGross + calc.fullOnly)}</Td>
              </tr>
            </tbody>
          </table>

          <div style={{
            display: "flex", gap: 20, padding: "10px 14px", borderTop: `1px solid ${C.line}`,
            fontSize: 11.5, color: C.ink2, flexWrap: "wrap", alignItems: "center",
          }}>
            <span><b>Click ALPHA/FULL</b> to move a feature between the two parts</span>
            <span style={{ color: C.red }}>🔴 = our estimate, not their words</span>
            <span><b>?</b> = cannot be sized until we look — never quoted fixed</span>
            <span><b>—</b> = their requirement, met by the engagement, not billed as software</span>
            <span style={{ color: C.ink3 }}>Users = who OPENS it, never how many records it touches</span>
          </div>
        </div>
      </div>
    </div>
  );
}
